import type { CondicionesTienda, EstadoCatalogo, MiAlcance } from '../types/metas.types';
import type { SeveridadTag } from './estado';
import { domingoDe, esFechaIso, esLunes, fechaCorta, lunesDe, ms, semanaEnFrase, sumarDias } from './semanas';

/**
 * Condiciones de tienda y suplentes (fase 3), en palabras.
 *
 * Los bonos que pide el spec de comisiones y que ningún sistema sabía decir:
 * - TRANSPORTE: tienda de difícil acceso. A TODO el personal de la tienda.
 * - BODEGUERO: bodega de más de un piso Y bodeguero fijo. Hacen falta las dos:
 *   en la semana 38 los 112 bodegueros quedaron «pendientes de catálogo» porque
 *   nadie podía decir qué tienda cumplía.
 * - SUPLENTE: por persona, en desarrollo hasta certificarse, máximo 3 meses.
 *
 * ⚠️ Aquí sólo se NOMBRA. Qué vale en qué semana lo deciden las funciones de
 * Postgres (`metas_condiciones_vigentes` / `metas_suplentes_vigentes`), que
 * también lee el cálculo: dos definiciones de «vigente» pagarían bonos
 * distintos por la misma semana.
 */

export const CAMPOS_CONDICION: (keyof CondicionesTienda)[] = ['dificilAcceso', 'bodegaMasDeUnPiso', 'bodegueroFijo'];

/**
 * Desde esta semana (S45) Transporte y Bodeguero salen SÓLO de la marca de cada
 * persona; antes, también de la condición vigente de la tienda. Espejo de
 * `MARCAS_POR_PERSONA_DESDE` de la API (`domain/condiciones.ts`) y de
 * `metas-fuente-db.cjs`: si una se mueve, se mueven las tres.
 */
export const MARCAS_POR_PERSONA_DESDE = '2026-11-02';

export const ETIQUETA_CONDICION: Record<keyof CondicionesTienda, string> = {
  dificilAcceso: 'Tienda de difícil acceso',
  bodegaMasDeUnPiso: 'Bodega de más de un piso',
  bodegueroFijo: 'Bodeguero fijo',
};

/** Qué bonos dan unas condiciones: espejo de `marcasDeCondicion` de la API, sin el puesto de cada persona. */
export function bonosDe(c: CondicionesTienda | null | undefined): { transporte: boolean; bodeguero: boolean } {
  if (!c) return { transporte: false, bodeguero: false };
  return { transporte: !!c.dificilAcceso, bodeguero: !!c.bodegaMasDeUnPiso && !!c.bodegueroFijo };
}

/** «Transporte y Bodeguero» · «Transporte» · «Bodeguero» · «Ningún bono». */
export function textoBonos(c: CondicionesTienda | null | undefined): string {
  const b = bonosDe(c);
  if (b.transporte && b.bodeguero) return 'Transporte y Bodeguero';
  if (b.transporte) return 'Transporte';
  if (b.bodeguero) return 'Bodeguero';
  return 'Ningún bono';
}

/**
 * Por qué NO da el bono de Bodeguero cuando sólo se marcó una de las dos, o
 * `null`. Marcar «bodega de más de un piso» sin «bodeguero fijo» parece dar el
 * bono y no lo da: se dice al marcarlo, no en la nómina.
 */
export function faltaParaBodeguero(c: CondicionesTienda): string | null {
  if (c.bodegaMasDeUnPiso && !c.bodegueroFijo)
    return 'Sin bodeguero fijo no hay bono de Bodeguero: hacen falta las dos.';
  if (!c.bodegaMasDeUnPiso && c.bodegueroFijo)
    return 'Con la bodega en un solo piso no hay bono de Bodeguero: hacen falta las dos.';
  return null;
}

export const mismasCondiciones = (a: CondicionesTienda, b: CondicionesTienda): boolean =>
  a.dificilAcceso === b.dificilAcceso && a.bodegaMasDeUnPiso === b.bodegaMasDeUnPiso && a.bodegueroFijo === b.bodegueroFijo;

export const ETIQUETA_ESTADO_CATALOGO: Record<EstadoCatalogo, string> = {
  propuesta: 'Propuesta',
  autorizada: 'Autorizada',
  rechazada: 'Rechazada',
  retirada: 'Retirada',
};

export const SEVERIDAD_ESTADO_CATALOGO: Record<EstadoCatalogo, SeveridadTag> = {
  propuesta: 'warning',
  autorizada: 'success',
  rechazada: 'danger',
  retirada: 'secondary',
};

/**
 * El lunes más antiguo que la cuenta puede usar, cuando la API no lo dice
 * (`GET suplentes` no lo trae): el primero de la ventana; sin límite para el
 * administrador. Es la misma regla que `vigenteDesdeMinimo` de la API. La API
 * vuelve a revisarlo: esto sólo evita ofrecer lo que va a negar.
 */
export function minimoDeAlcance(alcance: Pick<MiAlcance, 'esAdmin' | 'ventana'>): string | null {
  if (alcance.esAdmin) return null;
  return alcance.ventana[0] ?? null;
}

/** Cuántas semanas hacia atrás se ofrecen al administrador (él no tiene mínimo) y hacia adelante a todos. */
export const SEMANAS_ATRAS_ADMIN = 12;
export const SEMANAS_ADELANTE_CONDICION = 12;

/**
 * Los lunes que se ofrecen para «vigente desde»: del mínimo de la cuenta a doce
 * semanas adelante. `incluir` agrega lunes ya elegidos (la propuesta abierta)
 * aunque caigan fuera, para que el valor que se edita no desaparezca de la
 * lista; los anteriores al mínimo NO se agregan (la API los negaría).
 */
export function lunesParaCondicion(x: {
  minimo: string | null;
  semanaActual: string;
  incluir?: (string | null | undefined)[];
}): string[] {
  const desde = x.minimo ?? sumarDias(x.semanaActual, -7 * SEMANAS_ATRAS_ADMIN);
  const base = x.minimo && x.minimo > x.semanaActual ? x.minimo : x.semanaActual;
  const hasta = sumarDias(base, 7 * SEMANAS_ADELANTE_CONDICION);
  const out = new Set<string>();
  for (let l = lunesDe(desde) < desde ? sumarDias(lunesDe(desde), 7) : lunesDe(desde); l <= hasta; l = sumarDias(l, 7))
    out.add(l);
  for (const l of x.incluir ?? []) if (l && esLunes(l) && (!x.minimo || l >= x.minimo)) out.add(l);
  return Array.from(out).sort((a, b) => ms(a) - ms(b));
}

/**
 * Límites del inicio del desarrollo de un suplente, como los revisa la API: no
 * antes del mínimo de la cuenta (pagaría semanas cerradas) ni después del
 * domingo de la última semana de la ventana.
 */
export function limitesInicioDesarrollo(alcance: Pick<MiAlcance, 'esAdmin' | 'ventana'>): {
  min: string | null;
  max: string | null;
} {
  const ultima = alcance.ventana[alcance.ventana.length - 1];
  return { min: minimoDeAlcance(alcance), max: ultima ? domingoDe(ultima) : null };
}

/**
 * Lo que el diálogo de suplente revisa antes de mandar, en palabras, o `null`.
 * Son las reglas de fecha de la API (`revisarFechasSuplente` y
 * `revisarInicioEnVentana`); la API las vuelve a revisar al guardar.
 */
export function problemaFechasSuplente(x: {
  inicio: string;
  certificado: string;
  min: string | null;
  max: string | null;
  hoy: string;
}): string | null {
  if (!esFechaIso(x.inicio)) return 'Escribe la fecha de inicio del desarrollo.';
  if (x.min && x.inicio < x.min)
    return `El desarrollo puede registrarse desde el ${fechaCorta(x.min)}: un inicio anterior haría cobrar el bono en semanas ya cerradas. Si empezó antes, pide a un administrador que lo registre con su fecha real.`;
  if (x.max && x.inicio > x.max)
    return `El desarrollo no puede empezar después del ${fechaCorta(x.max)} (el final de la ventana de captura).`;
  if (!x.certificado) return null;
  return problemaCertificacion(x.certificado, x.inicio, x.hoy);
}

/** La certificación es un hecho: no antes del inicio, no futura. */
export function problemaCertificacion(cert: string, inicio: string, hoy: string): string | null {
  if (!esFechaIso(cert)) return 'Escribe la fecha de certificación.';
  if (cert < inicio)
    return `La certificación no puede ser anterior al inicio del desarrollo (${fechaCorta(inicio)}).`;
  if (cert > hoy) return 'La certificación no puede ser futura: se registra cuando ocurre.';
  return null;
}

/**
 * Primera semana (lunes) en que un suplente certificado en `cert` ya NO cobra.
 * Espejo de la API: `metas_suplentes_vigentes` lo paga mientras
 * `certificado_en > lunes`, así que certificado en lunes deja de cobrar esa
 * misma semana y cualquier otro día, la siguiente.
 */
export const primeraSemanaSinBono = (cert: string): string => (esLunes(cert) ? cert : sumarDias(lunesDe(cert), 7));

/**
 * Que la certificación no toque semanas cerradas, en palabras, o `null`. Espejo
 * de `revisarCertificacionEnVentana` de la API, que responde 409 si:
 * - con la fecha nueva el bono dejaría de pagarse en una semana anterior al
 *   mínimo (le quitaría semanas ya pagadas), o
 * - la certificación que ya tiene cerró semanas anteriores al mínimo:
 *   cambiarla, a la fecha que sea, volvería a pagarlas.
 * `minimo` es el de `minimoDeAlcance` (`null` = administrador: sin límite).
 *
 * ⚠️ La API revisa primero la fecha nueva; aquí va primero la registrada,
 * porque bloquea CUALQUIER fecha: decir «esa fecha cierra una semana» mandaba a
 * probar otras que la API también iba a negar.
 */
export function problemaCertificacionEnVentana(cert: string, anterior: string | null, minimo: string | null): string | null {
  if (!minimo) return null;
  if (!puedeCorregirCertificacion(anterior, minimo))
    return `La certificación registrada (${fechaCorta(anterior)}) ya dejó sin bono semanas anteriores a la del ${fechaCorta(minimo)}, que están cerradas: cambiarla volvería a pagarlas. Si hay que corregirla, pídelo a un administrador.`;
  const desde = primeraSemanaSinBono(cert);
  if (desde < minimo)
    return `Con la certificación del ${fechaCorta(cert)} el bono dejaría de pagarse desde la ${semanaEnFrase(desde)}, que ya está cerrada: la primera que puede cerrarse es la del ${fechaCorta(minimo)}. Si esa es la fecha real, pide a un administrador que la registre.`;
  return null;
}

/**
 * Si la certificación registrada todavía se puede corregir desde esta cuenta:
 * no, si ya dejó de pagar semanas anteriores al mínimo. Con ella la captura
 * esconde «Corregir certificación», que la API negaría con cualquier fecha.
 */
export const puedeCorregirCertificacion = (anterior: string | null, minimo: string | null): boolean =>
  !anterior || !minimo || primeraSemanaSinBono(anterior) >= minimo;

/**
 * La fecha de certificación más temprana que la API acepta: no antes del inicio
 * y, para quien no es administrador, no antes del martes previo al mínimo (el
 * lunes de antes dejaría de pagar esa semana, que ya se cerró).
 */
export function certificacionMinima(inicio: string, minimo: string | null): string {
  if (!minimo) return inicio;
  const martes = sumarDias(minimo, -6);
  return martes > inicio ? martes : inicio;
}
